/**
 * ROREN PDP editorial gallery + complete-the-set interactions.
 * Cart writes stay on Shopify's /cart/add.js endpoint.
 */
(function () {
  'use strict';

  function formatMoney(cents, symbol) {
    var amount = Math.round(Number(cents || 0)) / 100;
    var hasCents = Math.round(Number(cents || 0)) % 100 !== 0;
    return (symbol || '$') + amount.toLocaleString('en-AU', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0
    });
  }

  function setStatus(root, message) {
    var node = root.querySelector('[data-roren-set-status]');
    if (node) node.textContent = message || '';
  }

  function galleryParts(gallery) {
    return {
      list: gallery.querySelector('.product__media-list'),
      items: Array.prototype.slice.call(
        gallery.querySelectorAll('.product__media-list > .product__media-item')
      ),
      thumbnails: Array.prototype.slice.call(
        gallery.querySelectorAll('.thumbnail-list__item[data-target]')
      ),
      previous: gallery.querySelector('[id^="GalleryViewer-"] .slider-button--prev'),
      next: gallery.querySelector('[id^="GalleryViewer-"] .slider-button--next')
    };
  }

  function galleryIndex(gallery) {
    var parts = galleryParts(gallery);
    if (!parts.list || !parts.items.length) return 0;
    var pendingIndex = Number(gallery.dataset.rorenGalleryTarget);
    if (Number.isFinite(pendingIndex)) {
      return Math.max(0, Math.min(parts.items.length - 1, pendingIndex));
    }
    return Math.max(
      0,
      Math.min(
        parts.items.length - 1,
        Math.round(parts.list.scrollLeft / Math.max(parts.list.clientWidth, 1))
      )
    );
  }

  function reduceGalleryMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileGallery() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function centerGalleryThumbnail(thumbnail) {
    var list = thumbnail.closest('.thumbnail-list');
    if (!list) return;
    var left =
      thumbnail.offsetLeft - (list.clientWidth - thumbnail.offsetWidth) / 2;
    var maxLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    left = Math.max(0, Math.min(maxLeft, left));
    if (Math.abs(list.scrollLeft - left) < 1) return;
    list.scrollTo({
      left: left,
      behavior: reduceGalleryMotion() ? 'auto' : 'smooth'
    });
  }

  function primeGalleryImages(parts, index) {
    [index - 1, index, index + 1].forEach(function (itemIndex) {
      var item = parts.items[itemIndex];
      var image = item && item.querySelector('img');
      if (image && image.getAttribute('loading') !== 'eager') {
        image.setAttribute('loading', 'eager');
      }
    });
  }

  function primeGalleryTarget(gallery, targetId) {
    var parts = galleryParts(gallery);
    var targetIndex = parts.items.findIndex(function (item) {
      return item.dataset.mediaId === targetId;
    });
    if (targetIndex >= 0) primeGalleryImages(parts, targetIndex);
  }

  function syncGallery(gallery, index) {
    var parts = galleryParts(gallery);
    if (!parts.items.length) return;
    var safeIndex = Math.max(0, Math.min(parts.items.length - 1, index));
    gallery.dataset.rorenGalleryIndex = String(safeIndex);
    primeGalleryImages(parts, safeIndex);

    parts.items.forEach(function (item, itemIndex) {
      item.classList.toggle('is-active', itemIndex === safeIndex);
    });

    parts.thumbnails.forEach(function (thumbnail) {
      var active = thumbnail.dataset.target === parts.items[safeIndex].dataset.mediaId;
      var button = thumbnail.querySelector('button');
      if (button) {
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      }
      if (active && window.matchMedia('(max-width: 749px)').matches) {
        centerGalleryThumbnail(thumbnail);
      }
    });

    if (parts.previous) parts.previous.disabled = safeIndex <= 0;
    if (parts.next) parts.next.disabled = safeIndex >= parts.items.length - 1;
  }

  function positionGallery(gallery, index) {
    var parts = galleryParts(gallery);
    if (!parts.list || !parts.items.length) return;
    var target = Math.max(0, Math.min(parts.items.length - 1, index));
    var targetLeft = parts.items[target].offsetLeft;

    gallery.dataset.rorenGalleryTarget = String(target);

    if (!isMobileGallery() || reduceGalleryMotion()) {
      parts.list.scrollTo({
        left: targetLeft,
        behavior: isMobileGallery() ? 'auto' : 'smooth'
      });
      delete gallery.dataset.rorenGalleryTarget;
      syncGallery(gallery, target);
      return;
    }

    window.clearTimeout(gallery._rorenGallerySwitchTimer);
    parts.list.classList.remove('is-revealing');
    parts.list.classList.add('is-switching');

    gallery._rorenGallerySwitchTimer = window.setTimeout(function () {
      parts.list.scrollTo({ left: targetLeft, behavior: 'auto' });
      delete gallery.dataset.rorenGalleryTarget;
      syncGallery(gallery, target);
      parts.list.classList.remove('is-switching');
      void parts.list.offsetWidth;
      parts.list.classList.add('is-revealing');
      window.clearTimeout(gallery._rorenGalleryRevealTimer);
      gallery._rorenGalleryRevealTimer = window.setTimeout(function () {
        parts.list.classList.remove('is-revealing');
      }, 210);
    }, 90);
  }

  function moveGallery(gallery, direction) {
    var parts = galleryParts(gallery);
    if (!parts.list || !parts.items.length) return;
    var target = Math.max(
      0,
      Math.min(parts.items.length - 1, galleryIndex(gallery) + direction)
    );
    positionGallery(gallery, target);
  }

  function jumpToGalleryItem(gallery, targetId) {
    var parts = galleryParts(gallery);
    var targetIndex = parts.items.findIndex(function (item) {
      return item.dataset.mediaId === targetId;
    });
    if (targetIndex < 0) return;
    positionGallery(gallery, targetIndex);
  }

  function openGalleryMedia(gallery, mediaId) {
    var opener = Array.prototype.slice
      .call(gallery.querySelectorAll('.product__media-toggle[data-media-id]'))
      .find(function (button) {
        return button.dataset.mediaId === String(mediaId);
      });
    if (opener) opener.click();
  }

  function selectedItems(root) {
    return Array.prototype.slice.call(
      root.querySelectorAll('[data-roren-set-item][data-selected="true"]')
    );
  }

  function updateTotal(root) {
    var cents = Number(root.dataset.mainPrice || 0);
    selectedItems(root).forEach(function (item) {
      cents += Number(item.dataset.price || 0);
    });

    var total = root.querySelector('[data-roren-set-total]');
    if (total) total.textContent = formatMoney(cents, root.dataset.currencySymbol);

    var add = root.querySelector('[data-roren-set-add]');
    if (add) add.disabled = selectedItems(root).length === 0;
  }

  function selectVariant(root, swatch) {
    var item = swatch.closest('[data-roren-set-item]');
    if (!item) return;

    item.dataset.variantId = swatch.dataset.variantId || '';
    item.dataset.price = swatch.dataset.price || '0';
    item.dataset.variantTitle = swatch.dataset.variantTitle || '';

    item.querySelectorAll('[data-roren-set-swatch]').forEach(function (button) {
      var selected = button === swatch;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    var label = item.querySelector('[data-roren-set-variant-label]');
    if (label) label.textContent = swatch.dataset.variantTitle || '';

    var price = item.querySelector('[data-roren-set-price]');
    if (price) price.textContent = formatMoney(swatch.dataset.price, root.dataset.currencySymbol);

    var image = item.querySelector('.roren-set-card__media img');
    if (image && swatch.dataset.image) {
      image.removeAttribute('srcset');
      image.src = swatch.dataset.image;
    }

    updateTotal(root);
  }

  function toggleItem(item, root) {
    var selected = item.dataset.selected !== 'true';
    item.dataset.selected = selected ? 'true' : 'false';
    item.classList.toggle('is-selected', selected);

    var button = item.querySelector('[data-roren-set-toggle]');
    if (button) button.setAttribute('aria-pressed', selected ? 'true' : 'false');

    updateTotal(root);
  }

  function currentMainVariantId(root) {
    var forms = Array.prototype.slice.call(
      document.querySelectorAll('form[action*="/cart/add"]')
    );
    var form =
      forms.find(function (candidate) {
        var formId = candidate.getAttribute('id') || '';
        return formId.indexOf('product-form-') === 0;
      }) ||
      forms.find(function (candidate) {
        return candidate.querySelector('[name="add"], .product-form__submit');
      });
    var input = form ? form.querySelector('[name="id"]') : null;
    return input ? input.value : root.dataset.mainVariantId;
  }

  async function addSet(root, button) {
    var mainId = currentMainVariantId(root);
    var items = selectedItems(root).map(function (item) {
      return Number(item.dataset.variantId);
    });

    if (mainId) items.unshift(Number(mainId));
    items = items.filter(function (id, index, all) {
      return Number.isFinite(id) && id > 0 && all.indexOf(id) === index;
    });

    if (!items.length) return;

    var original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    setStatus(root, '');

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: items.map(function (id) {
            return { id: id, quantity: 1 };
          })
        })
      });

      if (!response.ok) {
        var error = await response.json().catch(function () { return {}; });
        throw new Error(error.description || 'Could not add this set.');
      }

      button.textContent = 'Set added';

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'roren-complete-set' });
      }

      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        window.setTimeout(function () {
          button.disabled = false;
          button.textContent = original;
          updateTotal(root);
        }, 1200);
      } else {
        window.location.href = '/cart';
      }
    } catch (error) {
      setStatus(root, error.message || 'Could not add this set.');
      button.disabled = false;
      button.textContent = original;
      updateTotal(root);
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      var gallery = event.target.closest('.product--editorial-gallery media-gallery');
      if (gallery && gallery.dataset.suppressEditorialClick === 'true') {
        event.preventDefault();
        event.stopPropagation();
        delete gallery.dataset.suppressEditorialClick;
        return;
      }

      var prev = event.target.closest(
        '.product--editorial-gallery [id^="GalleryViewer-"] .slider-button--prev'
      );
      var next = event.target.closest(
        '.product--editorial-gallery [id^="GalleryViewer-"] .slider-button--next'
      );
      if (prev || next) {
        event.preventDefault();
        event.stopPropagation();
        moveGallery((prev || next).closest('media-gallery'), prev ? -1 : 1);
        return;
      }

      var thumbnail = event.target.closest(
        '.product--editorial-gallery .thumbnail-list__item[data-target]'
      );
      if (thumbnail) {
        event.preventDefault();
        event.stopPropagation();
        jumpToGalleryItem(thumbnail.closest('media-gallery'), thumbnail.dataset.target);
        return;
      }

      var additionalMedia = event.target.closest('[data-roren-gallery-media-open]');
      if (additionalMedia) {
        event.preventDefault();
        event.stopPropagation();
        openGalleryMedia(additionalMedia.closest('media-gallery'), additionalMedia.dataset.mediaId);
      }
    },
    true
  );

  document.addEventListener('click', function (event) {
    var galleryTrigger = event.target.closest('[data-roren-gallery-open]');
    if (galleryTrigger) {
      var gallery = galleryTrigger.closest('media-gallery');
      var opener = gallery && gallery.querySelector('.product__media-item .product__media-toggle');
      if (opener) {
        event.preventDefault();
        opener.click();
      }
      return;
    }

    var root = event.target.closest('[data-roren-complete-set]');
    if (!root) return;

    var swatch = event.target.closest('[data-roren-set-swatch]');
    if (swatch) {
      selectVariant(root, swatch);
      return;
    }

    var toggle = event.target.closest('[data-roren-set-toggle]');
    if (toggle) {
      var item = toggle.closest('[data-roren-set-item]');
      if (item) toggleItem(item, root);
      return;
    }

    var add = event.target.closest('[data-roren-set-add]');
    if (add) addSet(root, add);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var roots = event.target.querySelectorAll('[data-roren-complete-set]');
    roots.forEach(updateTotal);
  });

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.rorenEditorialReady === 'true') return;
    var parts = galleryParts(gallery);
    if (!parts.list || !parts.items.length) return;
    gallery.dataset.rorenEditorialReady = 'true';

    var frame = 0;
    parts.list.addEventListener(
      'scroll',
      function () {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(function () {
          if (gallery.dataset.rorenGalleryTarget != null) return;
          var index = galleryIndex(gallery);
          if (gallery.dataset.rorenGalleryIndex === String(index)) return;
          syncGallery(gallery, index);
        });
      },
      { passive: true }
    );

    var drag = null;
    parts.list.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = {
        startX: event.clientX,
        startLeft: parts.list.scrollLeft,
        distance: 0,
        moved: false
      };
    });
    parts.list.addEventListener('pointermove', function (event) {
      if (!drag) return;
      var distance = event.clientX - drag.startX;
      drag.distance = distance;
      if (!drag.moved && Math.abs(distance) < 8) return;
      drag.moved = true;
      parts.list.classList.add('is-dragging');
      if (parts.list.setPointerCapture) parts.list.setPointerCapture(event.pointerId);
      parts.list.scrollLeft = drag.startLeft - distance;
      event.preventDefault();
    });

    function finishDrag(event) {
      if (!drag) return;
      if (drag.moved) {
        gallery.dataset.suppressEditorialClick = 'true';
        window.setTimeout(function () {
          delete gallery.dataset.suppressEditorialClick;
        }, 60);
      }
      parts.list.classList.remove('is-dragging');
      if (drag.moved && Math.abs(drag.distance) >= 32) {
        var index = galleryIndex(gallery) + (drag.distance < 0 ? 1 : -1);
        syncGallery(gallery, index);
        var targetItem = galleryParts(gallery).items[index];
        if (targetItem) {
          parts.list.scrollTo({ left: targetItem.offsetLeft, behavior: 'smooth' });
        }
      }
      if (parts.list.releasePointerCapture && event.pointerId != null) {
        try {
          parts.list.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture may already be released when the pointer leaves the page.
        }
      }
      drag = null;
    }

    parts.list.addEventListener('pointerup', finishDrag);
    parts.list.addEventListener('pointercancel', finishDrag);

    parts.thumbnails.forEach(function (thumbnail) {
      ['pointerenter', 'pointerdown', 'focus'].forEach(function (eventName) {
        thumbnail.addEventListener(eventName, function () {
          primeGalleryTarget(gallery, thumbnail.dataset.target);
        });
      });
    });

    gallery.addEventListener('shopify:block:select', function () {
      syncGallery(gallery, galleryIndex(gallery));
    });
    syncGallery(gallery, galleryIndex(gallery));
  }

  function initGalleries(scope) {
    var galleries = (scope || document).querySelectorAll('.product--editorial-gallery media-gallery');
    galleries.forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initGalleries(document); });
  } else {
    initGalleries(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initGalleries(event.target);
  });
})();
